import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { H1, H2 } from "@/components/ui/headings"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { useLazyQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z, ZodType } from "zod"

type FormValues = {
  code: string
}

export const ValidatePasswordResetToken = gql(`
  query ValidatePasswordResetToken($email: String!, $resetToken: String!) {
    validatePasswordResetToken(email: $email, resetToken: $resetToken) {
      errors {
        path
        fullMessages
      }
    }
  }
`)

const schema: ZodType<FormValues> = z.object({
  code: z.string().min(1, "Code is required"),
})

const ForgotPasswordCodeForm = ({
  email,
  setCode,
}: {
  email: string
  setCode: (code: string) => void
}) => {
  const { toast } = useToast()
  const [validatePasswordResetToken] = useLazyQuery(ValidatePasswordResetToken)

  const defaultValues: FormValues = {
    code: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState } = methods

  const onSubmit = async (values: FormValues) => {
    const { data } = await validatePasswordResetToken({
      variables: {
        email: email,
        resetToken: values.code,
      },
    })

    const errors = data?.validatePasswordResetToken.errors ?? []

    if (errors?.length <= 0) {
      setCode(values.code)
    } else {
      toast({
        title: errors[0].fullMessages[0],
        variant: "destructive",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="mb-12">
        <H1>Enter Your Reset Code</H1>
        <H2>
          We sent you a code to your email address. Check your messages and
          enter it here.
        </H2>
      </div>

      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="code"
            control={control}
            placeholder="000000"
            type="code"
            label="Code"
            autoFocus={true}
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Confirm
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default ForgotPasswordCodeForm
